import { useEffect, useState } from "react";
import { useMarketingStore } from "@/zustand/emailMarketingStore.js";
import { useProductData } from "@/zustand/productStore.js";
import { ClockIcon, TruckIcon } from "@/utils/miscIcons";
import Timer from "@/components/Timer";
import DoubleTagIcon from "@/public/fallbacks/DoubleTagIcon.svg";
import { useQuizModalStore } from "@/zustand/quizModalStore";
import { setCookie, getCookie } from "@/utils/helpers";
import { useCartData } from "@/zustand/cartStore.js";
import { isDiscountActive, isDiscountPopupMode, replaceDiscountPercentVariable } from "@/utils/discounts";

const Bars = ({ isProductHeroActive }) => {
  const { freeShippingEnabled, freeShippingApplied, remainingTime } = useMarketingStore();
  const { isQuizCompleted } = useQuizModalStore();
  const { selectedProduct, isSubscribe, discountCode, isDiscountApplied } = useCartData();
  const productData = useProductData();

  const [shippingTimerData, setShippingTimerData] = useState(null);
  const [shouldShowShippingTimer, setShouldShowShippingTimer] = useState(true);

  const content = window.post_template.checkout_v4_meta_bars;
  const onlyLabel = content.tr_checkout_v4_meta_bars_only_label;
  const itemLabel = content.tr_checkout_v4_meta_bars_itemsLabel;
  const leftInStock = content.tr_checkout_v4_meta_bars_leftInStock_label;
  const productPickerV2 = productData?.show_product_picker_v2;
  const activeBars = content.checkout_v4_active_meta_bars;
  const quizDiscountLabel =
    window.post_template.checkout_v4_quiz.tr_quiz_loader_checkout_discount_label || "Checkout Discount";
  const quizDiscountLabelColor =
    window.post_template.checkout_v4_quiz.quiz_loader_checkout_discount_label_color || "#00B83440";
  const discountPopup = window.post_template.checkout_v4_discount_popup;
  const discountPopupLabel = replaceDiscountPercentVariable(discountPopup?.tr_applied_label, selectedProduct);
  const discountPopupLabelColor = discountPopup?.applied_label_color || "#00B83440";
  const isDiscountPopupApplied =
    isDiscountPopupMode(productData) && isDiscountActive(productData, isSubscribe, discountCode, isDiscountApplied);
  const orderSummaryVersion = window.post_template.checkout_v4_summary.checkout_v4_summary_version;
  const [totalSupply, setTotalSupply] = useState();
  const [numberOfItems, setNumberOfItems] = useState();

  const timeRangeFrom = 20;
  const timeRangeTo = 30;
  const randomTotalSupply = Math.floor(Math.random() * 100 + 400);
  const randomCurrentStock = Math.floor(Math.random() * (107 - 95) + 95);
  const randomInterval = getRandomTime(timeRangeFrom, timeRangeTo);

  let startIncrement;
  const percents = calculatePercentageDecrease(numberOfItems, totalSupply);

  function getRandomTime(min, max) {
    return Math.floor(Math.random() * (Number(max) - Number(min))) + Number(min);
  }

  async function simulateIncrement() {
    const randomNumber = Math.floor(Math.random() * (9 - 7)) + 7;
    setNumberOfItems((prevNumberOfItems) => {
      if (prevNumberOfItems <= 5 || prevNumberOfItems - randomNumber <= 5) {
        clearInterval(startIncrement);
        return 5;
      } else {
        return Math.max(prevNumberOfItems - randomNumber);
      }
    });
  }

  function calculatePercentageDecrease(numberOfItems, totalSupply) {
    const newPercentage = (numberOfItems * 100) / totalSupply;
    return 100 - newPercentage;
  }

  const isEnhancedContrast = window.enhancedContrast;

  const [timerData, setTimerData] = useState(null);
  const [shouldShowTimer, setShouldShowTimer] = useState(true);

  useEffect(() => {
    setTotalSupply(randomTotalSupply);
    setNumberOfItems(randomCurrentStock);
    startIncrement = setInterval(simulateIncrement, randomInterval * 1000);
  }, []);

  useEffect(() => {
    let t = getCookie("checkout_timer");
    const now = Date.now();
    const DURATION = (content?.checkout_v4_meta_bars_timeRemainingBar_time ?? 10) * 60 * 1000;
    const COOLDOWN = 24 * 60 * 60 * 1000;

    if (!t || now > t.cooldownExpires) {
      t = {
        endAt: now + DURATION,
        cooldownExpires: now + DURATION + COOLDOWN,
      };

      setCookie("checkout_timer", t, t.cooldownExpires);
      setShouldShowTimer(true);
    } else if (now > t.endAt) {
      setShouldShowTimer(false);
    }

    setTimerData(t);
  }, []);

  useEffect(() => {
    if (!freeShippingEnabled || !freeShippingApplied) return;

    let t = getCookie("shipping_timer");
    const now = Date.now();
    const COOLDOWN = 24 * 60 * 60 * 1000;
    const DURATION = remainingTime * 60 * 1000;

    if (!t || now > t.endAt) {
      t = {
        endAt: now + DURATION,
        cooldownExpires: now + DURATION + COOLDOWN,
      };

      setCookie("shipping_timer", t, t.cooldownExpires);
      setShouldShowShippingTimer(true);
    } else if (now > t.endAt) {
      setShouldShowShippingTimer(false);
    }

    setShippingTimerData(t);
  }, [freeShippingEnabled, freeShippingApplied, remainingTime]);

  const handleShippingTimerEnd = () => {
    setShouldShowShippingTimer(false);
  };

  const handleTimerEnd = () => {
    setShouldShowTimer(false);
  };

  return (
    <div
      className={`bars__container ${productPickerV2 && "bars__container--productPickerV2"} ${orderSummaryVersion && "bars__container--summaryV2"} ${isProductHeroActive ? "bars__container--productHero" : ""}`}
    >
      {isQuizCompleted && (
        <div
          key="offer"
          className={`bars bars__bar_2 ${isEnhancedContrast && "enhancedContrast"}`}
          style={{ backgroundColor: `${quizDiscountLabelColor}` }}
        >
          <img src={DoubleTagIcon} height={19} width={19} />
          <span>{quizDiscountLabel}</span>
        </div>
      )}
      {isDiscountPopupApplied && discountPopupLabel && (
        <div
          key="discount-popup"
          className={`bars bars__bar_2 ${isEnhancedContrast && "enhancedContrast"}`}
          style={{ backgroundColor: `${discountPopupLabelColor}` }}
        >
          <img src={DoubleTagIcon} height={19} width={19} alt="" />
          <span>{discountPopupLabel}</span>
        </div>
      )}
      {activeBars.map((bar) => {
        if (bar === "stock") {
          return (
            <div key="stock" className={`bars bars__bar_1 ${isEnhancedContrast && "enhancedContrast"}`}>
              <div className={`bars__bar_1_leftText ${isEnhancedContrast && "enhancedContrast"}`}>
                {onlyLabel} <span className="bars__bar_1_leftText_red">{numberOfItems}</span> {itemLabel}
              </div>
              <span className="bars__bar_1__colorBar">
                <span className={`bars__bar_1__colorBar_colored `} style={{ width: `${percents}%` }}></span>
              </span>
              <div className={`bars__bar_1_rightText ${isEnhancedContrast && "enhancedContrast"}`}>{leftInStock}</div>
            </div>
          );
        }
        if (bar === "offer") {
          return (
            <div key="offer" className={`bars bars__bar_2 ${isEnhancedContrast && "enhancedContrast"}`}>
              <img src={DoubleTagIcon} height={19} width={19} />
              <span>{content.tr_checkout_v4_meta_bars_discountBarLabel}</span>
            </div>
          );
        }
        if (bar === "timer" && shouldShowTimer && timerData) {
          return (
            <div key="timer" className={`bars bars__bar_3 ${isEnhancedContrast && "enhancedContrast"}`}>
              <ClockIcon />
              <span className="bars__bar_3_clockLabel">
                {content?.tr_checkout_v4_meta_bars_timeRemainingBarLabel.split("xx:xx")[0]}
                <span className={`bars__bar_3_clockTime ${isEnhancedContrast && "enhancedContrast"}`}>
                  <Timer endAt={timerData.endAt} onEnd={handleTimerEnd} />
                </span>
                {content.tr_checkout_v4_meta_bars_timeRemainingBarLabel.split("xx:xx")[1]}
              </span>
            </div>
          );
        }
        return null;
      })}
      {freeShippingApplied && freeShippingEnabled && shouldShowShippingTimer && shippingTimerData && (
        <div className="bars bars__bar_4">
          <TruckIcon />
          <span>
            {content?.tr_checkout_v4_meta_bars_free_shipping_expiration_label.split("xx:xx")[0]}
            <span className="bars__bar_3_clockTime">
              <Timer endAt={shippingTimerData.endAt} onEnd={handleShippingTimerEnd} />
            </span>
            {content.tr_checkout_v4_meta_bars_free_shipping_expiration_label.split("xx:xx")[1]}
          </span>
        </div>
      )}
    </div>
  );
};

export default Bars;
